import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { ChangeEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { FileType } from "src/api/types";
import ImageUploader from "src/components/ImageUploader";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import { convertToBlob } from "src/utils/convertToBlob";
import IdConfirmation from "../VouchedVerification/components/IdConfirmation";
import { useUploadFileMutation } from "src/api/services/bucketApi";
import { usePatient } from "src/context/PatientProvider";
import { useUpdateMeMutation } from "src/api/services/patientApi";
import { useTrack } from "src/hooks/useTrack";
import { useProduct } from "src/context/ProductProvider";

interface Props {
  fileName: string;
  cardFace: "front" | "back";
}

const InsuranceUpload = ({ fileName, cardFace }: Props) => {
  const isMobile = useIsMobile();
  const { submitAnswer } = useSubmitAnswers();
  const [uploadFile] = useUploadFileMutation();
  const [updatePatient] = useUpdateMeMutation();
  const track = useTrack();
  const productSlug = useProduct((p) => p.slug);
  const paConsent = usePatient(
    (patient) => patient.prior_auth_consent ?? false,
  );
  const [file, setFile] = useState<FileType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const page = "insurance-upload";

  const next = useCallback(() => {
    submitAnswer(["Ignore"]);
    setFile(null);
  }, [submitAnswer]);

  const handlePaConsent = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      await updatePatient({ prior_auth_consent: e.target.checked });
    },
    [updatePatient],
  );

  const onConfirm = useCallback(async () => {
    if (!file) {
      setError("Please select the image you want to upload");
      return;
    }
    setLoading(true);

    const blob = await convertToBlob(file.fileToUpload);

    track(`${cardFace}-insurance-uploaded`, { product: productSlug });

    return uploadFile({
      file: blob,
      name: fileName,
      type: blob.type,
      folder: "insurance",
    })
      .then(next)
      .catch((err) => {
        setError((err as Error).message);
      })
      .finally(() => setLoading(false));
  }, [cardFace, file, fileName, next, productSlug, track, uploadFile]);

  const handleImage = useCallback(
    (image: FileType | null) => {
      setFile(image);
      setError("");
    },
    [setFile],
  );

  const deleteFile = useCallback(() => {
    handleImage(null);
  }, [handleImage]);

  return (
    <Stack gap={2}>
      <Stack gap={2}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
          {t(
            `${page}.root.${file ? `title2_${cardFace}` : `title_${cardFace}`}`,
          )}
        </Typography>
        <Typography variant={isMobile ? "subtitle2" : "subtitle1"}>
          {t(`${page}.root.subtitle`)}
        </Typography>
      </Stack>
      <Stack>
        <ImageUploader
          title={null}
          subtitle={null}
          name={fileName}
          file={file}
          folder="insurance"
          setFilePath={handleImage}
          subImageTextTwo={`Click to upload or drag into here`}
          subImageTextMobile={`Click to upload or drag into here`}
        />
      </Stack>
      {error ? (
        <Typography sx={{ color: "red" }} textAlign="center">
          {error}
        </Typography>
      ) : null}
      {file && (
        <IdConfirmation
          deleteFile={deleteFile}
          onConfirm={onConfirm}
          loading={loading}
          type={"insurance"}
        />
      )}

      <Typography variant={isMobile ? "subtitle2" : "subtitle1"}>
        {t(`${page}.root.subtext`)}
      </Typography>
      {cardFace === "front" ? (
        <FormControlLabel
          sx={{
            alignItems: "center",
            gap: 1,
            margin: 0,
            color: "#6C7278",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Checkbox
              checked={paConsent}
              onChange={handlePaConsent}
              sx={{
                padding: 0,
                "&.Mui-checked": {
                  color: "#FF8175",
                },
              }}
            />
          }
          label={t(`${page}.checkbox`)}
        />
      ) : null}
    </Stack>
  );
};

export default InsuranceUpload;
